import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import {
    AUDIT_TYPES,
    LEGACY_FRAMEWORKS,
    FrameworkOption,
    DateRange,
    AuditInfo
} from './auditor-export.types';
import { useTenancy } from '@/contexts/TenancyContext';
import { 
    getAllFrameworks, 
    frameworksToOptions, 
    filterEnabledFrameworks,
    type FrameworkOption as ModuleFrameworkOption 
} from '@/services/modulesService';

interface ReportJobResponse {
    success: boolean;
    jobId?: string;
    status?: 'queued' | 'processing' | 'completed' | 'failed';
    progress?: number;
    downloadUrl?: string;
    expiresAt?: string;
    error?: string;
    message?: string;
}

export const useAuditorExport = (companyID: string) => {
    const { enabledModules, isModuleEnabled } = useTenancy();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [jobId, setJobId] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<ReportJobResponse | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);
    
    const [frameworks, setFrameworks] = useState<FrameworkOption[]>([]);
    const [allFrameworks, setAllFrameworks] = useState<ModuleFrameworkOption[]>([]);
    
    // Fetch all frameworks from modules collection and filter by enabled modules
    useEffect(() => {
        const loadFrameworks = async () => {
            try {
                setIsLoading(true);
                
                // Fetch all frameworks from the modules collection
                const moduleFrameworks = await getAllFrameworks();
                console.log('[useAuditorExport] Fetched frameworks from modules collection:', moduleFrameworks.length);
                
                // Convert to framework options
                const frameworkOptions = frameworksToOptions(moduleFrameworks);
                setAllFrameworks(frameworkOptions);
                
                // Filter to only show enabled frameworks
                const enabledFrameworkOptions = filterEnabledFrameworks(
                    frameworkOptions,
                    enabledModules,
                    isModuleEnabled
                );
                
                console.log('[useAuditorExport] Enabled framework options:', enabledFrameworkOptions);
                
                // If no enabled frameworks found, fall back to legacy frameworks filtered by enabled modules
                let finalFrameworks: FrameworkOption[];
                if (enabledFrameworkOptions.length > 0) {
                    finalFrameworks = enabledFrameworkOptions.map(fw => ({
                        id: fw.id,
                        label: fw.label,
                        slug: fw.slug,
                        dbKey: fw.dbKey,
                        checked: false
                    }));
                } else {
                    // Fallback to legacy frameworks filtered by enabled modules
                    console.log('[useAuditorExport] No enabled frameworks from modules, falling back to legacy');
                    const legacyOptions = LEGACY_FRAMEWORKS.map(fw => ({
                        id: fw.id,
                        label: fw.label,
                        dbKey: fw.dbKey,
                        slug: undefined
                    }));
                    const filteredLegacy = filterEnabledFrameworks(legacyOptions, enabledModules, isModuleEnabled);
                    finalFrameworks = filteredLegacy.map(fw => ({
                        id: fw.id,
                        label: fw.label,
                        slug: fw.slug,
                        dbKey: fw.dbKey,
                        checked: false
                    }));
                }
                
                console.log('[useAuditorExport] Final frameworks to display:', finalFrameworks);
                setFrameworks(finalFrameworks);
            } catch (err) {
                console.error('[useAuditorExport] Error loading frameworks:', err);
                setError(err instanceof Error ? err : new Error('Failed to load frameworks'));
                
                // Fallback to legacy frameworks on error
                const legacyOptions = LEGACY_FRAMEWORKS.map(fw => ({
                    id: fw.id,
                    label: fw.label,
                    dbKey: fw.dbKey,
                    slug: undefined
                }));
                const filteredLegacy = filterEnabledFrameworks(legacyOptions, enabledModules, isModuleEnabled);
                setFrameworks(filteredLegacy.map(fw => ({
                    id: fw.id,
                    label: fw.label,
                    slug: fw.slug,
                    dbKey: fw.dbKey,
                    checked: false
                })));
            } finally {
                setIsLoading(false);
            }
        };
        
        loadFrameworks();
    }, [enabledModules, isModuleEnabled]);

    const [dateRange, setDateRange] = useState<DateRange>({
        startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
    });

    const [auditInfo, setAuditInfo] = useState<AuditInfo>({
        typeOfAudit: '',
        startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
    });

    // Clean up SSE connection on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    const toggleFramework = useCallback((id: string) => {
        setFrameworks(prev =>
            prev.map(fw =>
                fw.id === id ? { ...fw, checked: !fw.checked } : fw
            )
        );
    }, []);

    const handleDateChange = useCallback((field: 'startDate' | 'endDate', value: string) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleAuditInfoChange = useCallback((field: keyof typeof auditInfo, value: string) => {
        setAuditInfo(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const connectToJobStream = useCallback(async (id: string) => {
        // Close existing connection if any
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        try {
            // Use deiterateAPI to create authenticated EventSource
            console.log('[SSE] Creating EventSource for job:', id);
            const eventSource = await deiterateAPI.createEventSource(`/v1/reports/auditor-pack/${id}/stream`, {
                backend: 'thalassa',
            });

            console.log('[SSE] EventSource created, readyState:', eventSource.readyState);
            eventSourceRef.current = eventSource;

            eventSource.onopen = () => {
                console.log('[SSE] Connection opened');
            };

            eventSource.onmessage = (event) => {
                console.log('[SSE] Message received:', event.data);
                try {
                    const data = JSON.parse(event.data);
                    
                    if (data.type === 'connected') {
                        console.log('[SSE] Connected to job stream');
                        return;
                    }

                    console.log('[SSE] Processing update:', data);

                    // Update job status
                    const newStatus = {
                        success: true,
                        jobId: id,
                        status: data.status,
                        progress: data.progress,
                        downloadUrl: data.downloadUrl,
                        error: data.error,
                    };
                    console.log('[SSE] Setting job status:', newStatus);
                    setJobStatus(newStatus);

                    if (data.status === 'completed' && data.downloadUrl) {
                        eventSource.close();
                        eventSourceRef.current = null;
                        
                        setIsSuccess(true);
                        setIsLoading(false);
                        toast.success('Report ready! Starting download...');
                        
                        // Trigger download
                        const a = document.createElement('a');
                        a.href = data.downloadUrl;
                        a.download = data.fileName || `AuditorPack_${new Date().toISOString().split('T')[0]}.zip`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                    } else if (data.status === 'failed') {
                        eventSource.close();
                        eventSourceRef.current = null;
                        
                        setError(new Error(data.error || 'Report generation failed'));
                        setIsLoading(false);
                        toast.error(data.error || 'Report generation failed');
                    }
                } catch (err) {
                    console.error('[SSE] Error parsing message:', err);
                }
            };

            eventSource.onerror = (err) => {
                console.error('[SSE] Connection error:', err);
                eventSource.close();
                eventSourceRef.current = null;
                
                // Don't show error if job is already completed
                if (jobStatus?.status !== 'completed') {
                    setError(new Error('Lost connection to server'));
                    setIsLoading(false);
                }
            };
        } catch (err) {
            console.error('[SSE] Failed to create EventSource:', err);
            setError(new Error('Failed to connect to server'));
            setIsLoading(false);
        }
    }, [jobStatus?.status]);

    const resetState = useCallback(() => {
        setIsSuccess(false);
        setIsLoading(false);
        setIsExporting(false);
        setError(null);
        setJobId(null);
        setJobStatus(null);
        
        // Close any existing SSE connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
    }, []);

    const exportAuditPack = useCallback(async () => {
        const selectedFrameworks = frameworks
            .filter(fw => fw.checked)
            .map(fw => fw.id);

        if (selectedFrameworks.length === 0) {
            setError(new Error('Please select at least one framework'));
            return false;
        }

        try {
            setIsExporting(true);
            setIsLoading(true);
            setError(null);
            setIsSuccess(false);

            // Call Thalassa API to start background generation
            const response = await deiterateAPI.POST<ReportJobResponse>('/v1/reports/auditor-pack', {
                backend: 'thalassa',
                body: {
                    frameworks: selectedFrameworks,
                    dateRange,
                    auditInfo,
                },
            });

            if (!response.success || !response.jobId) {
                throw new Error(response.error || 'Failed to start report generation');
            }

            setJobId(response.jobId);
            setJobStatus(response);
            
            toast.success(response.message || 'Report generation started. You will be notified when ready.');
            
            // Connect to SSE stream for real-time updates
            connectToJobStream(response.jobId);

            return true;
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(new Error(errorMessage));
            setIsLoading(false);
            toast.error(`Failed to start export: ${errorMessage}`);
            return false;
        } finally {
            setIsExporting(false);
        }
    }, [frameworks, dateRange, auditInfo, connectToJobStream]);

    return {
        isLoading,
        isExporting,
        error,
        frameworks,
        dateRange,
        auditInfo,
        toggleFramework,
        handleDateChange,
        handleAuditInfoChange,
        exportAuditPack,
        isSuccess,
        jobId,
        jobStatus,
        resetState,
    };
};
